import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { authService } from '@/services/auth';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await authService.requestPasswordReset(email.trim());
      setDone(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Forgot password - Arrangelly</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-violet-50/30 to-slate-100 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md bg-white/95 border border-slate-200 shadow-lg shadow-slate-200/50 rounded-2xl p-8 backdrop-blur-md">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Forgot password</h1>
          <p className="text-sm text-slate-600 mb-6">
            Enter your email and we&apos;ll send you a link to reset your password.
          </p>
          {done ? (
            <div className="space-y-4">
              <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-sm">
                If an account exists for that email, we&apos;ve sent instructions. Check your inbox and spam
                folder.
              </div>
              <Link to="/login" className="inline-block text-violet-600 hover:text-violet-700 underline text-sm">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-600 mb-1" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="you@example.com"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full mt-2 bg-accent hover:bg-accent/90 text-accent-foreground disabled:opacity-50"
                >
                  {busy ? 'Sending…' : 'Send reset link'}
                </Button>
              </form>
              <p className="mt-6 text-xs text-slate-500 text-center">
                <Link to="/login" className="text-violet-600 hover:text-violet-700 underline">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordPage;
